/**
 * CDSTriagePanel
 *
 * Proactive Clinical Decision Support for the right panel.
 *
 * Real recommendations + derived signals — no placeholder content. Every card
 * maps to a real backend signal or a real patient-data computation. Each card
 * terminates in an existing modal flow (prescribe / lab order / schedule).
 *
 * Sources:
 *   - aiRecommendationService (markdown reasoning from /intellihealth/.../recommendations)
 *   - prescriptionService     (active rx → next refill window)
 *   - Patient.medication_allergies (allergy guard surfaced as INFO when present)
 *
 * Triage:
 *   ACTION (teal)   — Rx recommendation present
 *   WATCH  (amber)  — Labs recommended OR refill due within 14 days
 *   INFO   (slate)  — Allergy summary, drug count, follow-up reminders
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  FileText,
  FlaskConical,
  Loader2,
  Pill,
  RefreshCw,
  ShieldAlert,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  aiRecommendationService,
  parseRecommendation,
} from '@/services/aiRecommendationService';
import { prescriptionService } from '@/services/prescriptionService';
import { cn } from '@/lib/utils';

type Severity = 'action' | 'watch' | 'info';
type ActionType = 'prescribe' | 'order_lab' | 'schedule' | 'view_labs' | 'dismiss';

interface CDSCard {
  id: string;
  severity: Severity;
  headline: string;
  evidence?: string;
  citations?: string[];
  actions: { type: ActionType; label: string }[];
}

/**
 * Placement mode controls density and chrome:
 *   - 'inline' — center column, prominent, full evidence + citations + actions
 *   - 'rail'   — right side panel, compact, used for the passive background queue
 */
type PlacementMode = 'inline' | 'rail';

interface CDSTriagePanelProps {
  patientId: string | null;
  patientName?: string | null;
  /** Restrict to specific severities. Defaults to all. */
  severityFilter?: Severity[];
  /** Placement mode — drives sizing, header treatment, and section chrome. */
  mode?: PlacementMode;
  medicationAllergies?: string[] | null;
  onPrescribe?: () => void;
  onOrderLab?: () => void;
  onSchedule?: () => void;
}

const SEVERITY_STYLES: Record<Severity, {
  border: string;
  bg: string;
  pillBg: string;
  pillText: string;
  iconColor: string;
  buttonClass: string;
  label: string;
}> = {
  action: {
    border: 'border-teal-200',
    bg: 'bg-gradient-to-br from-teal-50/80 to-white',
    pillBg: 'bg-teal-100',
    pillText: 'text-teal-800',
    iconColor: 'text-teal-600',
    buttonClass: 'border-teal-300 text-teal-700 hover:bg-teal-50',
    label: 'ACTION',
  },
  watch: {
    border: 'border-amber-200',
    bg: 'bg-gradient-to-br from-amber-50/70 to-white',
    pillBg: 'bg-amber-100',
    pillText: 'text-amber-800',
    iconColor: 'text-amber-600',
    buttonClass: 'border-amber-300 text-amber-800 hover:bg-amber-50',
    label: 'WATCH',
  },
  info: {
    border: 'border-slate-200',
    bg: 'bg-white',
    pillBg: 'bg-slate-100',
    pillText: 'text-slate-700',
    iconColor: 'text-slate-500',
    buttonClass: 'border-slate-200 text-slate-700 hover:bg-slate-50',
    label: 'INFO',
  },
};

const ACTION_ICONS: Record<ActionType, React.ReactNode> = {
  prescribe: <Pill className="h-3.5 w-3.5" />,
  order_lab: <FlaskConical className="h-3.5 w-3.5" />,
  schedule: <Calendar className="h-3.5 w-3.5" />,
  view_labs: <FlaskConical className="h-3.5 w-3.5" />,
  dismiss: <X className="h-3.5 w-3.5" />,
};

const SEVERITY_ICON: Record<Severity, React.ElementType> = {
  action: AlertTriangle,
  watch: AlertTriangle,
  info: CheckCircle2,
};

/** Pull citation tokens (e.g. "ADA 2025", "PEP-014") from a span of text. */
function extractCitations(text: string): string[] {
  const out = new Set<string>();
  const patterns = [
    /\b(?:ADA|AHA|ASCO|NCCN|USPSTF|FDA|AACE|NICE|CDC|WHO)(?:\s+\d{4})?\b/g,
    /\b(?:Endocrine Society|American Heart Association|American Diabetes Association)(?:\s+\d{4})?/g,
    /\b[A-Z]{2,5}-\d{2,4}\b/g,
    /\b(?:Guideline|Protocol|Standards of Care)\s+\d{4}\b/gi,
  ];
  patterns.forEach((re) => {
    const m = text.match(re);
    if (m) m.forEach((s) => out.add(s.trim()));
  });
  return Array.from(out).slice(0, 4);
}

interface ParsedRecs {
  meds: string[];
  labs: string[];
  followUp: string | null;
  fullText: string;
}

function parseReasoning(raw: string | object | null): ParsedRecs {
  if (!raw) return { meds: [], labs: [], followUp: null, fullText: '' };
  const text = typeof raw === 'string' ? raw : JSON.stringify(raw);

  const meds: string[] = [];
  const medRegex = /\*\*([^*]+)\*\*[^-\n]*[-–—]\s*(.+)/g;
  let m: RegExpExecArray | null;
  while ((m = medRegex.exec(text)) !== null) {
    meds.push(`${m[1].trim()} — ${m[2].trim()}`.slice(0, 140));
  }

  const labs: string[] = [];
  const labSection = text.match(
    /(?:Recommended Laboratory|Labs?\s*(?:Needed|Recommended))([\s\S]*?)(?:\n###|\n##|$)/i,
  );
  if (labSection) {
    labSection[1]
      .split('\n')
      .filter((l) => l.trim().startsWith('-') || l.trim().startsWith('•'))
      .forEach((l) => {
        const cleaned = l.replace(/^[-•*]\s*\*?\*?/, '').replace(/\*\*/g, '').trim();
        if (cleaned) labs.push(cleaned);
      });
  }

  let followUp: string | null = null;
  const fu = text.match(/(?:Follow[- ]?up|Schedule)([\s\S]*?)(?:\n###|\n##|$)/i);
  if (fu) {
    const first = fu[1].trim().split('\n')[0].replace(/^[-•*]\s*/, '').trim();
    if (first) followUp = first;
  }

  return { meds, labs, followUp, fullText: text };
}

const CDSTriagePanel: React.FC<CDSTriagePanelProps> = ({
  patientId,
  medicationAllergies,
  severityFilter,
  mode = 'rail',
  onPrescribe,
  onOrderLab,
  onSchedule,
}) => {
  const isInline = mode === 'inline';
  const [reasoning, setReasoning] = useState<string | object | null>(null);
  const [activeRxCount, setActiveRxCount] = useState<number | null>(null);
  const [nearestRefillDays, setNearestRefillDays] = useState<number | null>(null);
  const [recStatus, setRecStatus] = useState<'ready' | 'processing' | 'pending_data' | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!patientId) return;
    setIsLoading(true);
    setError(null);

    try {
      const [recRes, rxRes] = await Promise.allSettled([
        aiRecommendationService.getPatientRecommendations(patientId, 3),
        prescriptionService.getPrescriptionsByPatientId(patientId, { page: 1, limit: 25 }),
      ]);

      if (recRes.status === 'fulfilled' && recRes.value.success) {
        setReasoning(parseRecommendation(recRes.value.data?.recommendation ?? null));
        setRecStatus(recRes.value.data?.meta?.status ?? 'ready');
      } else if (recRes.status === 'fulfilled') {
        setRecStatus(recRes.value.data?.meta?.status ?? null);
      }

      if (rxRes.status === 'fulfilled') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = rxRes.value;
        // API envelope is { success, data: { prescriptions: [...], pagination } }.
        // Probe each plausible shape and always coerce to an array.
        const candidates = [
          payload?.data?.data?.prescriptions,
          payload?.data?.prescriptions,
          payload?.data?.data,
          payload?.data,
          payload,
        ];
        const raw = candidates.find((c) => Array.isArray(c)) ?? [];
        const list: Array<{ status?: string; created_at?: string; followup_days?: number | null }>
          = raw as Array<{ status?: string; created_at?: string; followup_days?: number | null }>;
        const active = list.filter((p) => (p?.status ?? '').toLowerCase() === 'active');
        setActiveRxCount(active.length);

        const now = Date.now();
        const days = active
          .map((p) => {
            if (!p.created_at) return null;
            const created = new Date(p.created_at).getTime();
            const window = (p.followup_days ?? 30) * 86_400_000;
            return Math.round((created + window - now) / 86_400_000);
          })
          .filter((n): n is number => n !== null);
        setNearestRefillDays(days.length > 0 ? Math.min(...days) : null);
      }
    } catch (e) {
      console.error('[CDS Triage] failed to load:', e);
      setError('Unable to load recommendations');
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const cards = useMemo<CDSCard[]>(() => {
    const out: CDSCard[] = [];
    const parsed = parseReasoning(reasoning);
    const citations = parsed.fullText ? extractCitations(parsed.fullText) : [];

    if (parsed.meds.length > 0) {
      out.push({
        id: 'rx-rec',
        severity: 'action',
        headline:
          parsed.meds.length === 1
            ? 'Medication recommendation'
            : `${parsed.meds.length} medication recommendations`,
        evidence: parsed.meds.slice(0, 2).join(' · '),
        citations,
        actions: [
          { type: 'prescribe', label: 'Prescribe' },
          { type: 'dismiss', label: 'Defer' },
        ],
      });
    }

    if (parsed.labs.length > 0) {
      out.push({
        id: 'lab-rec',
        severity: 'watch',
        headline: `${parsed.labs.length} lab${parsed.labs.length > 1 ? 's' : ''} recommended`,
        evidence: parsed.labs.slice(0, 3).join(', '),
        citations: citations.length > 0 ? citations : undefined,
        actions: [
          { type: 'order_lab', label: 'Order labs' },
          { type: 'dismiss', label: 'Dismiss' },
        ],
      });
    }

    if (nearestRefillDays !== null && nearestRefillDays <= 14 && nearestRefillDays >= 0) {
      out.push({
        id: 'refill-window',
        severity: nearestRefillDays <= 7 ? 'watch' : 'info',
        headline:
          nearestRefillDays === 0
            ? 'Refill due today'
            : `Refill window: ${nearestRefillDays} day${nearestRefillDays === 1 ? '' : 's'}`,
        evidence:
          'Active prescription approaching refill threshold. Confirm tolerance and renew or adjust.',
        actions: [
          { type: 'prescribe', label: 'Renew' },
          { type: 'dismiss', label: 'Dismiss' },
        ],
      });
    }

    if (parsed.followUp) {
      out.push({
        id: 'followup',
        severity: 'info',
        headline: 'Follow-up recommended',
        evidence: parsed.followUp,
        actions: [
          { type: 'schedule', label: 'Schedule' },
          { type: 'dismiss', label: 'Dismiss' },
        ],
      });
    }

    const allergies = (medicationAllergies ?? []).filter(Boolean);
    if (allergies.length > 0) {
      out.push({
        id: 'allergy-guard',
        severity: 'info',
        headline: 'Allergy guard active',
        evidence: `On file: ${allergies.slice(0, 4).join(', ')}. Verified before any prescribe action.`,
        actions: [{ type: 'dismiss', label: 'Acknowledge' }],
      });
    }

    if (out.length === 0 && activeRxCount !== null) {
      out.push({
        id: 'drug-count',
        severity: 'info',
        headline:
          activeRxCount === 0
            ? 'No active medications'
            : `${activeRxCount} active medication${activeRxCount === 1 ? '' : 's'}`,
        evidence:
          activeRxCount === 0
            ? 'Patient has no current prescriptions on file.'
            : 'Review the medications panel before prescribing to avoid duplicates.',
        actions:
          activeRxCount === 0
            ? [{ type: 'prescribe', label: 'Start prescription' }]
            : [{ type: 'dismiss', label: 'Acknowledge' }],
      });
    }

    const filterSet = severityFilter && severityFilter.length > 0 ? new Set(severityFilter) : null;
    return out.filter((c) => !dismissed.has(c.id) && (!filterSet || filterSet.has(c.severity)));
  }, [reasoning, nearestRefillDays, activeRxCount, medicationAllergies, dismissed, severityFilter]);

  const handleAction = (card: CDSCard, type: ActionType) => {
    if (type === 'dismiss') {
      setDismissed((prev) => {
        const next = new Set(prev);
        next.add(card.id);
        return next;
      });
      return;
    }
    if (type === 'prescribe') onPrescribe?.();
    else if (type === 'order_lab') onOrderLab?.();
    else if (type === 'schedule') onSchedule?.();
  };

  if (!patientId) return null;

  const stateWrap = isInline
    ? 'rounded-lg border border-slate-200 bg-white px-4 py-3'
    : 'px-3 py-2.5 border-b border-slate-200 bg-slate-50/40';

  if (isLoading) {
    return (
      <div className={stateWrap}>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Analyzing patient data…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={stateWrap}>
        <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <ShieldAlert className="h-3.5 w-3.5 text-slate-400" />
            {error}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => void load()}
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  if (cards.length === 0) {
    if (recStatus === 'pending_data') {
      return (
        <div className={stateWrap}>
          <div className="flex items-start gap-2 text-xs text-slate-500">
            <FileText className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
            <span>
              Awaiting more clinical data — recommendations will populate once
              labs or intake answers are available.
            </span>
          </div>
        </div>
      );
    }
    return null;
  }

  // ── Render variants per placement mode ────────────────────────────────────
  return (
    <div
      className={cn(
        isInline
          ? 'rounded-lg border border-slate-200 bg-white'
          : 'border-b border-slate-200 bg-slate-50/40',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between',
          isInline ? 'px-4 pt-3 pb-1.5' : 'px-3 pt-3 pb-1.5',
        )}
      >
        <span
          className={cn(
            'font-semibold uppercase tracking-wider',
            isInline ? 'text-[11px] text-slate-500' : 'text-[10px] text-slate-500',
          )}
        >
          {isInline ? 'Active Clinical Decision Support' : 'Background Queue'}
        </span>
        <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Live
        </span>
      </div>

      <div className={cn('space-y-2', isInline ? 'px-4 pb-4' : 'px-3 pb-3')}>
        {cards.map((card) => {
          const sev = SEVERITY_STYLES[card.severity];
          const Icon = SEVERITY_ICON[card.severity];
          return (
            <div
              key={card.id}
              className={cn(
                'rounded-lg border',
                sev.border,
                sev.bg,
                isInline ? 'p-3.5' : 'p-2.5',
              )}
            >
              <div className="flex items-start gap-2.5">
                <Icon
                  className={cn(
                    'mt-0.5 flex-shrink-0',
                    isInline ? 'h-4 w-4' : 'h-3.5 w-3.5',
                    sev.iconColor,
                  )}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 font-semibold tracking-wider',
                        sev.pillBg,
                        sev.pillText,
                        isInline ? 'text-[10px]' : 'text-[10px]',
                      )}
                    >
                      {sev.label}
                    </span>
                    <p
                      className={cn(
                        'font-medium text-slate-900 leading-tight',
                        isInline ? 'text-sm' : 'text-[13px]',
                      )}
                    >
                      {card.headline}
                    </p>
                  </div>
                  {card.evidence && (
                    <p
                      className={cn(
                        'leading-snug text-slate-600',
                        isInline ? 'mt-1.5 text-sm' : 'mt-1 text-xs',
                      )}
                    >
                      {card.evidence}
                    </p>
                  )}
                  {card.citations && card.citations.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {card.citations.map((c) => (
                        <span
                          key={c}
                          className="inline-flex items-center rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-500"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                  {card.actions.length > 0 && (
                    <div className={cn('flex items-center gap-1.5 flex-wrap', isInline ? 'mt-2.5' : 'mt-2')}>
                      {card.actions.map((a) =>
                        a.type === 'dismiss' ? (
                          <Button
                            key={a.type + a.label}
                            variant="ghost"
                            size="sm"
                            className={cn(
                              'text-slate-400 hover:text-slate-600',
                              isInline ? 'h-7 px-2 text-xs' : 'h-6 px-2 text-[11px]',
                            )}
                            onClick={() => handleAction(card, a.type)}
                          >
                            {a.label}
                          </Button>
                        ) : (
                          <Button
                            key={a.type + a.label}
                            variant={isInline && card.severity === 'action' ? 'default' : 'outline'}
                            size="sm"
                            className={cn(
                              isInline
                                ? card.severity === 'action'
                                  ? 'h-7 px-3 text-xs bg-teal-600 hover:bg-teal-700 text-white'
                                  : 'h-7 px-3 text-xs ' + sev.buttonClass
                                : 'h-6 px-2 text-[11px] ' + sev.buttonClass,
                            )}
                            onClick={() => handleAction(card, a.type)}
                          >
                            {ACTION_ICONS[a.type]}
                            <span className="ml-1">{a.label}</span>
                          </Button>
                        ),
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CDSTriagePanel;
